// SETTINGS > HOLIDAYS (holiday-list, M1, owner 2026-11-03).
//
// ONE company-wide list of non-working days. Its ONLY reader is the OEM-order
// "at risk" rule: "no acknowledgement within 2 working days" counts Mon-Fri
// minus the ACTIVE holidays here. The 3-days-to-promise rule stays calendar
// days, and nothing about a price, a quote or a document reads this list.
//
// Every role sees the list; only an Admin adds, renames, deactivates /
// reactivates or uploads. ⚠️ THE SCREEN IS NOT THE GUARD: every write route
// refuses Sales and Viewer with a 403 whatever this renders, and the server
// owns every rule (models/holiday.py) -- its messages are shown verbatim.
// A holiday's date is never edited and nothing is ever deleted. An upload
// SAVES NOTHING until confirmed, and the confirm is all-or-nothing.

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getHolidays, addHoliday, renameHoliday, setHolidayActive,
  previewHolidayUpload, importHolidayUpload, type Holiday, type HolidayPreview,
} from "@/services/api";
import { useIsAdminOrFalse } from "@/auth/AuthContext";
import { formatDate } from "@/lib/format";
import { colors, semanticColors, radius, spacing, heights, typography } from "@/styles/tokens";

const INPUT: React.CSSProperties = {
  height: heights.input, padding: `0 ${spacing[8]}px`,
  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  fontSize: typography.fontSize.base, backgroundColor: semanticColors.surface,
  color: semanticColors.textPrimary, boxSizing: "border-box",
};
const BUTTON: React.CSSProperties = {
  height: heights.control, padding: `0 ${spacing[14]}px`,
  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  fontSize: typography.fontSize.base, backgroundColor: semanticColors.surface,
  color: semanticColors.textPrimary, cursor: "pointer", whiteSpace: "nowrap",
};
const PRIMARY: React.CSSProperties = {
  ...BUTTON, border: "none", backgroundColor: colors.indigo600,
  color: semanticColors.onAccent, fontWeight: typography.fontWeight.medium,
};
const BOX: React.CSSProperties = {
  marginBottom: spacing[12], padding: spacing[12],
  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  backgroundColor: semanticColors.panelBg,
  display: "flex", flexDirection: "column", gap: spacing[8],
};
const NOTE: React.CSSProperties = { fontSize: typography.fontSize.sm, color: semanticColors.textSecondary };
const ERROR: React.CSSProperties = { ...NOTE, color: semanticColors.destructive };
const TH: React.CSSProperties = {
  textAlign: "left", padding: `${spacing[6]}px ${spacing[8]}px`,
  fontSize: typography.fontSize.xs, color: semanticColors.textMuted,
  fontWeight: typography.fontWeight.medium, borderBottom: `1px solid ${semanticColors.border}`,
  whiteSpace: "nowrap", backgroundColor: semanticColors.surface,
};
const TD: React.CSSProperties = {
  padding: `${spacing[6]}px ${spacing[8]}px`, fontSize: typography.fontSize.base,
  borderBottom: `1px solid ${semanticColors.border}`, whiteSpace: "nowrap", verticalAlign: "middle",
};
const PILL: React.CSSProperties = {
  display: "inline-flex", alignItems: "center", height: heights.pill, padding: `0 ${spacing[8]}px`,
  borderRadius: radius.base, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.medium,
  border: "1px solid",
};

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
/** The weekday of an ISO date, read as a calendar date (no time zone shift). */
function weekdayOf(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  return WEEKDAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()] ?? "";
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("The file could not be read."));
    reader.onload = () => {
      const text = String(reader.result ?? "");
      resolve(text.slice(text.indexOf(",") + 1));
    };
    reader.readAsDataURL(file);
  });
}

export function HolidaysTab() {
  const isAdmin = useIsAdminOrFalse();
  const [rows, setRows] = useState<Holiday[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [done, setDone] = useState("");
  const [busy, setBusy] = useState(false);

  const [newDate, setNewDate] = useState("");
  const [newName, setNewName] = useState("");
  const [addError, setAddError] = useState("");

  const [renaming, setRenaming] = useState<{ id: number; name: string } | null>(null);
  const [rowError, setRowError] = useState("");

  const [uploadOpen, setUploadOpen] = useState(false);
  const [file, setFile] = useState<{ name: string; b64: string } | null>(null);
  const [preview, setPreview] = useState<HolidayPreview | null>(null);
  const [uploadError, setUploadError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const r = await getHolidays();
    setLoading(false);
    if (!r.ok || !r.holidays) { setLoadError(r.error ?? "The holiday list could not be read."); return; }
    setLoadError("");
    setRows(r.holidays);
  }, []);
  useEffect(() => { void load(); }, [load]);

  const shown = useMemo(() => rows.filter((h) => showInactive || h.active), [rows, showInactive]);
  const activeCount = rows.filter((h) => h.active).length;

  async function onAdd() {
    setBusy(true);
    const r = await addHoliday(newDate, newName);
    setBusy(false);
    if (!r.ok || !r.holiday) { setAddError(r.error ?? "Not added."); return; }
    setAddError(""); setNewDate(""); setNewName("");
    setDone(`Added ${r.holiday.name} (${formatDate(r.holiday.date)}).`);
    void load();
  }

  async function onRename() {
    if (!renaming) return;
    setBusy(true);
    const r = await renameHoliday(renaming.id, renaming.name);
    setBusy(false);
    if (!r.ok) { setRowError(r.error ?? "Not renamed."); return; }
    setRowError(""); setRenaming(null);
    setDone("Holiday renamed.");
    void load();
  }

  async function onToggle(h: Holiday) {
    setBusy(true);
    const r = await setHolidayActive(h.id, !h.active);
    setBusy(false);
    if (!r.ok) { setRowError(r.error ?? "Not changed."); return; }
    setRowError("");
    setDone(`${h.name} ${h.active ? "deactivated" : "reactivated"}.`);
    void load();
  }

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    setPreview(null); setUploadError("");
    if (!f) { setFile(null); return; }
    try {
      const b64 = await fileToBase64(f);
      setFile({ name: f.name, b64 });
      const r = await previewHolidayUpload(f.name, b64);
      if (!r.ok || !r.preview) { setUploadError(r.error ?? "The file could not be previewed."); return; }
      setPreview(r.preview);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : "The file could not be read.");
    }
  }

  async function onConfirm() {
    if (!file) return;
    setBusy(true);
    const r = await importHolidayUpload(file.name, file.b64);
    setBusy(false);
    if (!r.ok) { setUploadError(r.error ?? "Nothing imported."); return; }
    setUploadOpen(false); setFile(null); setPreview(null); setUploadError("");
    setDone(`Imported ${r.preview?.summary.new ?? 0} holiday(s).`);
    void load();
  }

  const canConfirm = !!preview && preview.summary.error === 0 && preview.summary.new > 0 && !busy;

  return (
    <div data-testid="holidays-tab" style={{ display: "flex", flexDirection: "column" }}>
      <div data-testid="holidays-note" style={{ ...NOTE, marginBottom: spacing[12] }}>
        Company-wide non-working days. An OEM order is "at risk" when it has no acknowledgement
        within 2 working days of being placed: Monday to Friday, minus the active holidays listed
        here. Nothing else uses this list.
      </div>

      {isAdmin && (
        <div data-testid="holidays-add" style={BOX}>
          <div className="flex items-center" style={{ gap: spacing[8], flexWrap: "wrap" }}>
            <input data-testid="holidays-add-date" type="date" aria-label="Holiday date" value={newDate}
                   onChange={(e) => setNewDate(e.target.value)} style={INPUT} />
            <input data-testid="holidays-add-name" aria-label="Holiday name" placeholder="Name" value={newName}
                   maxLength={80} onChange={(e) => setNewName(e.target.value)} style={{ ...INPUT, flex: 1, minWidth: 0 }} />
            <button data-testid="holidays-add-save" style={PRIMARY} disabled={busy || !newDate || !newName.trim()}
                    onClick={() => void onAdd()}>Add holiday</button>
            <button data-testid="holidays-upload-open" style={BUTTON}
                    onClick={() => { setUploadOpen((o) => !o); setPreview(null); setFile(null); setUploadError(""); }}>
              Upload CSV / Excel
            </button>
          </div>
          {addError && <div data-testid="holidays-add-error" role="alert" style={ERROR}>{addError}</div>}
        </div>
      )}

      {isAdmin && uploadOpen && (
        <div data-testid="holidays-upload" style={BOX}>
          <div style={NOTE}>Columns <b>Date</b> (YYYY-MM-DD) and <b>Name</b>. The preview saves nothing; the
            upload is applied only if every row is valid. An upload never renames or reactivates a listed date.</div>
          <input data-testid="holidays-upload-file" type="file" accept=".csv,.xlsx" onChange={(e) => void onFile(e)} />
          {uploadError && <div data-testid="holidays-upload-error" role="alert" style={ERROR}>{uploadError}</div>}
          {preview && (
            <div data-testid="holidays-upload-preview" style={{ display: "flex", flexDirection: "column", gap: spacing[8] }}>
              <div data-testid="holidays-upload-summary" style={NOTE}>
                {preview.summary.new} new, {preview.summary.unchanged} already listed, {preview.summary.error} with errors
              </div>
              <table style={{ borderCollapse: "separate", borderSpacing: 0 }}>
                <tbody>
                  {preview.rows.map((r) => (
                    <tr key={r.index} data-testid={`holidays-upload-row-${r.index}`} data-action={r.action}>
                      <td style={TD}>{r.index}</td>
                      <td style={TD}>{r.date ?? ""}</td>
                      <td style={TD}>{r.name ?? ""}</td>
                      <td style={{ ...TD, color: r.action === "error" ? semanticColors.destructive : semanticColors.textSecondary }}>
                        {r.action === "error" ? r.error : r.action}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex" style={{ gap: spacing[8] }}>
                <button data-testid="holidays-upload-confirm" style={PRIMARY} disabled={!canConfirm}
                        onClick={() => void onConfirm()}>Apply</button>
                <button data-testid="holidays-upload-cancel" style={BUTTON}
                        onClick={() => { setUploadOpen(false); setPreview(null); setFile(null); }}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      {done && <div data-testid="holidays-done" style={{ ...NOTE, marginBottom: spacing[8] }}>{done}</div>}
      {loadError && <div data-testid="holidays-error" role="alert" style={{ ...ERROR, marginBottom: spacing[8] }}>{loadError}</div>}
      {rowError && <div data-testid="holidays-row-error" role="alert" style={{ ...ERROR, marginBottom: spacing[8] }}>{rowError}</div>}

      <div className="flex items-center" style={{ gap: spacing[12], marginBottom: spacing[8] }}>
        <label style={{ ...NOTE, display: "inline-flex", alignItems: "center", gap: spacing[6] }}>
          <input data-testid="holidays-show-inactive" type="checkbox" checked={showInactive}
                 onChange={(e) => setShowInactive(e.target.checked)} />
          Show inactive
        </label>
        <span style={NOTE}><span data-testid="holidays-active-count" className="tabular-nums">{activeCount}</span> active</span>
      </div>

      {loading ? (
        <div data-testid="holidays-loading" style={NOTE}>Loading holidays…</div>
      ) : !loadError && (
        <div style={{ overflowX: "auto" }}>
          <table data-testid="holidays-table" style={{ borderCollapse: "separate", borderSpacing: 0, width: "100%" }}>
            <thead>
              <tr>
                <th style={TH}>Date</th><th style={TH}>Day</th><th style={TH}>Name</th><th style={TH}>Status</th>
                {isAdmin && <th style={TH} aria-label="Actions" />}
              </tr>
            </thead>
            <tbody>
              {shown.map((h) => (
                <tr key={h.id} data-testid={`holiday-row-${h.date}`} data-active={h.active ? "true" : "false"}>
                  <td style={TD} className="tabular-nums">{formatDate(h.date)}</td>
                  <td style={TD}>{weekdayOf(h.date)}</td>
                  <td style={TD}>
                    {renaming?.id === h.id ? (
                      <span className="flex items-center" style={{ gap: spacing[6] }}>
                        <input data-testid={`holiday-rename-input-${h.date}`} aria-label="New name" value={renaming.name}
                               maxLength={80} onChange={(e) => setRenaming({ id: h.id, name: e.target.value })}
                               style={{ ...INPUT, minWidth: 0 }} />
                        <button data-testid={`holiday-rename-save-${h.date}`} style={PRIMARY} disabled={busy}
                                onClick={() => void onRename()}>Save</button>
                        <button style={BUTTON} onClick={() => { setRenaming(null); setRowError(""); }}>Cancel</button>
                      </span>
                    ) : h.name}
                  </td>
                  <td style={TD}>
                    {h.active
                      ? <span style={{ ...PILL, color: semanticColors.success, backgroundColor: semanticColors.successBg, borderColor: semanticColors.successBorder }}>Active</span>
                      : <span style={{ ...PILL, color: semanticColors.textMuted, backgroundColor: semanticColors.panelBg, borderColor: semanticColors.border }}>Inactive</span>}
                  </td>
                  {isAdmin && (
                    <td style={TD}>
                      <span className="flex" style={{ gap: spacing[6] }}>
                        <button data-testid={`holiday-rename-${h.date}`} style={BUTTON} disabled={busy}
                                onClick={() => setRenaming({ id: h.id, name: h.name })}>Rename</button>
                        <button data-testid={`holiday-toggle-${h.date}`} style={BUTTON} disabled={busy}
                                onClick={() => void onToggle(h)}>{h.active ? "Deactivate" : "Reactivate"}</button>
                      </span>
                    </td>
                  )}
                </tr>
              ))}
              {shown.length === 0 && (
                <tr><td colSpan={isAdmin ? 5 : 4} data-testid="holidays-empty" style={{ ...TD, color: semanticColors.textMuted }}>
                  {rows.length === 0 ? "No holidays listed: working days are Monday to Friday." : "No active holidays."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
